//
// utility functions

import { SchoolModel, UserProfileType } from '../../peekapak-types/DataProtocolTypes';
import {
  CurrentYearTeacher,
  DistrictData,
  DistrictDataBySchool,
  ReportStudent,
  ReportingMixpanelEvent,
  TeacherClassroomMaps,
} from '../../peekapak-types/ReportDataExchangeTypes';
import { PeekavilleJournalEntry } from '../../../peekapak-types/PeekavilleGameDataTypes';
import { Assignment } from '../../../peekapak-types/LessonPlanTypes';
import { logDebugLevel, noWhiteSpace } from '../GlobalFunctions';
import { EventName } from '../../peekapak-types/AnalyticsTypes';

export interface ChartDataCalculationParams {
  data: DistrictDataByType;
  startDate: Date;
  endDate: Date;
  schoolList: SchoolModel[];
  minBarWidth: number;
  gapWidth: number;
  containerWidth: number;
}

export type SchoolChartDataCalculationParams = Omit<ChartDataCalculationParams, 'data' | 'schoolList'>;

interface DistrictDataByType {
  number_of_educators: ReportingMixpanelEvent[];
  educator_logins: ReportingMixpanelEvent[];
  student_logins: ReportingMixpanelEvent[];
  story_opens: ReportingMixpanelEvent[];
  lessons_accessed: ReportingMixpanelEvent[];
  time_on_platform: ReportingMixpanelEvent[];
}

export const lookupNewDBStartDate = (event: string): Date => {
  const dateLibrary: {
    [key: string]: Date;
  } = {
    peekapakLogin: new Date('2023-08-10T00:00:00.000Z'),
    peekavilleLogin: new Date('2023-11-20T00:00:00.000Z'),
    platformEvents: new Date('2023-07-01T00:00:00.000Z'),
    journalMoodLog: new Date('2023-08-10T00:00:00.000Z'),
    startDate: new Date('2023-07-01T00:00:00.000Z'),
    completeDate: new Date('2023-11-20T00:00:00.000Z'),
  };
  return dateLibrary[event];
};

export function getNextMonthFirstDay(date: number | Date) {
  const asDate = (() => {
    if (date instanceof Date) {
      return date;
    }

    return new Date(date);
  })();

  return getNextMonth(new Date(asDate.getFullYear(), asDate.getMonth(), 1));
}
export function getNextMonth(date: number | Date) {
  const asDate = (() => {
    if (date instanceof Date) {
      return date;
    }

    return new Date(date);
  })();

  const monthNumber = asDate.getMonth();
  const dateNumber = asDate.getDate();
  const yearNumber = asDate.getFullYear();

  if (monthNumber + 1 > 11) {
    return new Date(yearNumber + 1, 0, dateNumber);
  }

  return new Date(yearNumber, monthNumber + 1, dateNumber);
}

export function getDateLastSchoolYear() {
  const year = new Date(Date.now()).getFullYear();
  const month = new Date(Date.now()).getMonth();
  if (month < 6) {
    return `${year - 2}-07-01`;
  }

  return `${year - 1}-07-01`;
}

export function getDateBeginningOfSchoolYear() {
  const year = new Date(Date.now()).getFullYear();
  const month = new Date(Date.now()).getMonth();
  if (month < 6) {
    return `${year - 1}-07-01`;
  }

  return `${year}-07-01`;
}

export function getDateTwoWeeksAgo() {
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  return twoWeeksAgo.toISOString().split('T')[0];
}

export function getToday() {
  return new Date(Date.now()).toISOString().split('T')[0];
}

export function getDateFromTimestamp(timestamp: any) {
  const d = new Date(timestamp);
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

export function getListOfSchoolYears() {
  const currentSchoolYear = (() => {
    const year = new Date(Date.now()).getFullYear();
    const month = new Date(Date.now()).getMonth();
    if (month < 6) {
      return year - 1;
    }
    return year;
  })();

  const listOfYears = [
    {
      id: `${currentSchoolYear - 1}-${currentSchoolYear}`,
      name: `${currentSchoolYear - 1}-${currentSchoolYear}`,
      startingYear: `${currentSchoolYear - 1}`,
      endingYear: `${currentSchoolYear}`,
    },
    {
      id: `${currentSchoolYear}-${currentSchoolYear + 1}`,
      name: `${currentSchoolYear}-${currentSchoolYear + 1}`,
      startingYear: `${currentSchoolYear}`,
      endingYear: `${currentSchoolYear + 1}`,
    },
  ];
  return listOfYears;
}

export function getDefaultSchoolYear() {
  const listOfYears = getListOfSchoolYears();

  const initialValue = listOfYears[listOfYears.length - 1].id;
  return initialValue;
}

export function getDefaultSchoolYearMinusOne() {
  const listOfYears = getListOfSchoolYears();
  const initialValue = listOfYears[listOfYears.length - 2].id;
  return initialValue;
}

export function getTimestampFromDate(dateStr: string) {
  const date = new Date(dateStr);
  const timestamp = date.getTime();
  return timestamp;
}

export function getStudentsToLoginsMap(eventsArray: Array<{ name: string; distinct_id: string; userId: string }>) {
  const loginEvents = eventsArray.filter((e) =>
    ['user login', 'interaction end', EventName.LOGIN, EventName.INTERACTION_END].includes(e.name),
  );
  const loginMap = loginEvents.reduce(
    (acc, event) => {
      if (acc[event.userId || event.distinct_id]) {
        return {
          ...acc,
          [`${event.userId || event.distinct_id}`]: acc[event.userId || event.distinct_id] + 1,
        };
      } else {
        return { ...acc, [`${event.userId || event.distinct_id}`]: 1 };
      }
    },
    {} as Record<string, number>,
  );
  return loginMap;
}

export function getNumberOfActiveStudents(eventsArray: ReportingMixpanelEvent[]) {
  const loginMap = getStudentsToLoginsMap(eventsArray);
  return Object.keys(loginMap).length;
}

export function getTotalRow(tableData: any[]) {
  const totalNum = tableData
    .map((row) => {
      const numbers = Object.values(row);
      numbers.shift();
      return numbers;
    }, 0)
    .reduce((a: { [x: string]: any }, b: any[]) => {
      b.forEach((num, i) => {
        a[i] = isNaN(num) ? a[i] : (a[i] || 0) + Number(num);
      });
      return a;
    }, []);
  totalNum.length && totalNum.unshift('Total');
  return totalNum.length ? totalNum : null;
}

//
// NB:
// this function assumes that teacherClassroomMaps is ready
// and userList is ready
//
export function getCurrentYearTeachersData(
  userList: UserProfileType[],
  teacherClassroomMaps: TeacherClassroomMaps,
  legacyTeacherData: ReportingMixpanelEvent[],
  newTeacherData: ReportingMixpanelEvent[],
): CurrentYearTeacher[] {
  const teacherList: UserProfileType[] = [];

  const currentYearTeachers = teacherClassroomMaps.maps.filter((teacher) => teacher.classrooms.length > 0);
  currentYearTeachers.forEach((teacher) => {
    const foundUser = userList.find((user) => user.email === teacher.email);
    if (foundUser) teacherList.push(foundUser);
  });

  const newTeacherList = teacherList.map((teacherObj) => {
    const legacyDBEvents = legacyTeacherData.filter((event) => event.distinct_id === teacherObj.email);

    const newDBEvents = newTeacherData.filter((event) => event.userId === teacherObj.userId);

    const combinedEvents = [...legacyDBEvents, ...newDBEvents];

    const loginTimes = getLegacyEducatorLogins(legacyDBEvents) + getNewEducatorLogins(newDBEvents);

    /*
    const filteredTeacherObj = Object.values(teacherData).filter(
      (item) => `${item.key}` === teacherObj.email
    );
    const loginTimes = filteredTeacherObj.length
      ? filteredTeacherObj[0].value
      : 0;
      */

    return {
      ...teacherObj,
      loginTimes,
      events: combinedEvents,
    };
  });

  return newTeacherList;
}

export function getNumberOfOtherTeachers(
  teacherClassroomMaps: TeacherClassroomMaps,
  teacherClassroomMapsReady: boolean,
) {
  if (teacherClassroomMapsReady) {
    return teacherClassroomMaps.maps.reduce((acc, m) => acc + (m.classrooms.length === 0 ? 1 : 0), 0);
  }

  return 0;
}

//
// NB:
// "Other students" means students who have mixpanel events but are not Rostered
//
export function getOtherStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
  mixpanelEvents: ReportingMixpanelEvent[],
  mixpanelEventsReady: boolean,
) {
  if (teacherStudentMapReady && mixpanelEventsReady) {
    const rosteredStudentList = Object.values(teacherStudentMap).flat();
    const studentEvents = mixpanelEvents.filter((event) => event.type === 'Student');
    const fromEventsStudentIdsSet = new Set(studentEvents.map((student) => student.userId));
    const fromEventsStudentIds = Array.from(fromEventsStudentIdsSet).filter(Boolean);

    const rosteredStudentIds = rosteredStudentList.map((student) => student.userId);

    const otherStudents = fromEventsStudentIds.filter((s) => !rosteredStudentIds.includes(s));
    console.debug(`otherStudents: ${otherStudents}`);
    return otherStudents;
  }
  return [];
}

export function getNumberOfOtherStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
  mixpanelEvents: ReportingMixpanelEvent[],
  mixpanelEventsReady: boolean,
) {
  const others = getOtherStudents(teacherStudentMap, teacherStudentMapReady, mixpanelEvents, mixpanelEventsReady);
  return others.length;
}

export function getNumberOfRosteredTeachers(
  teacherClassroomMaps: TeacherClassroomMaps,
  teacherClassroomMapsReady: boolean,
) {
  if (teacherClassroomMapsReady)
    return teacherClassroomMaps.maps.reduce((acc, m) => acc + (m.classrooms.length > 0 ? 1 : 0), 0);

  return 0;
}

export function getNumberOfRosteredStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
): number {
  if (teacherStudentMapReady) {
    const listOfTeachers = Object.keys(teacherStudentMap);
    const withStudents = listOfTeachers.filter((t) => teacherStudentMap[t].length > 0);
    const count = withStudents.reduce((acc, t) => {
      return acc + teacherStudentMap[t].length;
    }, 0);

    return count;
  }

  return 0;
}

export const secondaryUnits = [
  'welcome',
  'hswelcome',
  'identity',
  'hsidentity',
  'stress',
  'hsstress',
  'decision',
  'hsdecision',
  'belonging',
  'hsbelonging',
  'empathy',
  'hsempathy',
  'futureskills',
  'digitalliteracy',
];

export function getDistrictDataBySchool(data: ReportingMixpanelEvent[], schoolList: SchoolModel[]) {
  const isDataWithoutSchool = data.find((item) => item.schoolId === undefined);
  const dataBySchool = schoolList.map((school) =>
    school.combineIds
      ? data.filter((item) => school.combineIds?.includes(item.schoolId))
      : data.filter((item) => item.schoolId === school.id),
  );

  const dataIncludingNoSchool = isDataWithoutSchool
    ? [...dataBySchool, data.filter((item) => item.schoolId === undefined)]
    : dataBySchool;
  const parsedData = dataIncludingNoSchool.map((school, index) => {
    return {
      school_name: schoolList[index]?.name ? schoolList[index]?.name : 'Other',
      number_of_educators: getNumberOfEducators(school),
      educator_logins: getLegacyEducatorLogins(school) + getNewEducatorLogins(school),
      student_logins: getLegacyStudentLogins(school) + getNewStudentLogins(school),
      story_opens: getLegacyStoryOpens(school) + getNewStoryOpens(school),
      lessons_accessed: getLegacyLessonsAccessed(school) + getNewLessonsAccessed(school),
      time_on_platform: getLegacyTimeOnPlatform(school) + getNewTimeOnPlatform(school),
    };
  });
  return parsedData;
}

export function getNewDistrictDataBySchool(
  data: {
    number_of_educators: ReportingMixpanelEvent[];
    educator_logins: ReportingMixpanelEvent[];
    student_logins: ReportingMixpanelEvent[];
    story_opens: ReportingMixpanelEvent[];
    lessons_accessed: ReportingMixpanelEvent[];
    time_on_platform: ReportingMixpanelEvent[];
  },
  schoolList: SchoolModel[],
) {
  const schoolListWithOther = [
    ...schoolList,
    {
      name: 'Other',
      id: undefined,
    },
  ];

  const parsedData = schoolListWithOther.map((school, index) => {
    return {
      school_name: schoolList[index]?.name ? schoolList[index]?.name : 'Other',
      number_of_educators: new Set(
        Object.values(filterBySchool(data.number_of_educators, school).map((item) => item.userId)),
      ).size,
      educator_logins: filterBySchool(data.educator_logins, school).length,
      student_logins: filterBySchool(data.student_logins, school).length,
      story_opens: filterBySchool(data.story_opens, school).length,
      lessons_accessed: filterBySchool(data.lessons_accessed, school).length,
      time_on_platform: calculateTimeOnPlatform(filterBySchool(data.time_on_platform, school)),
    };
  });
  return parsedData;
}

export function getDistrictDataByType(
  legacyPeekavilleData: ReportingMixpanelEvent[],
  legacyPlatformData: ReportingMixpanelEvent[],
  newMixpanelData: ReportingMixpanelEvent[],
) {
  const combinedLegacyData = [...legacyPeekavilleData, ...legacyPlatformData];
  const dataObject = {
    number_of_educators: getEducatorEvents([...combinedLegacyData, ...newMixpanelData]),
    educator_logins: [
      ...getLegacyEducatorLoginEvents(legacyPlatformData),
      ...getNewEducatorLoginEvents(newMixpanelData),
    ],
    student_logins: [...getLegacyStudentLoginEvents(combinedLegacyData), ...getNewStudentLoginEvents(newMixpanelData)],
    story_opens: [...getLegacyStoryOpenEvents(combinedLegacyData), ...getNewStoryOpenEvents(newMixpanelData)],
    lessons_accessed: [
      ...getLegacyLessonsAccessedEvents(legacyPlatformData),
      ...getNewLessonsAccessedEvents(newMixpanelData),
    ],
    time_on_platform: [
      ...getLegacyTimeOnPlatformEvents(combinedLegacyData),
      ...getNewTimeOnPlatformEvents(newMixpanelData),
    ],
  };
  return dataObject;
}

const filterBySchool = (array: ReportingMixpanelEvent[], school: Partial<SchoolModel>) => {
  const filteredArray = school.combineIds
    ? array.filter((item) => school.combineIds?.includes(item.schoolId))
    : array.filter((item) => item.schoolId === school.id);
  return filteredArray;
};

const calculateTimeOnPlatform = (array: ReportingMixpanelEvent[]) => {
  const totalSessionDuration = Math.ceil(
    array.reduce(
      (acc, curr) =>
        (acc = curr?.duration
          ? acc + (curr.duration > 21600 ? parseInt(curr.duration) / 1000 : parseInt(curr.duration))
          : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

const getEducatorEvents = (events: ReportingMixpanelEvent[]) => {
  const teacherEvents = events.filter((item) => item.type === 'Teacher');
  return teacherEvents;
};

export const filterMixpanelEventsByDate = (startDate: Date, endDate: Date, array: ReportingMixpanelEvent[]) => {
  const filteredArray = array.filter((item) => new Date(item.time) >= startDate && new Date(item.time) <= endDate);
  return filteredArray;
};

export const filterStudentObjectByDate = (
  startDate: Date,
  endDate: Date,
  studentObject: Record<string, Array<PeekavilleJournalEntry | Assignment>>,
) => {
  const filteredArray = Object.entries(studentObject).map(([k, v]) => {
    return [
      k,
      v.filter((item) => {
        if (item.time) {
          return new Date(item.time) >= startDate && new Date(item.time) <= endDate;
        } else if (item.createdAt) {
          return new Date(item.createdAt) >= startDate && new Date(item.createdAt) <= endDate;
        }
      }),
    ];
  });
  const filteredObject = Object.fromEntries(filteredArray);
  return filteredObject;
};

const getStudentEvents = (events: ReportingMixpanelEvent[]) => {
  const teacherEvents = events.filter((item) => item.type === 'Student');
  return teacherEvents;
};

const getLegacyEducatorLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('peekapakLogin'))
    .filter((item) => item.name === 'User Login' || item.name === 'login');
  return oldDBLogins;
};

const getNewEducatorLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter(
    (item) => (item.type === 'Teacher' || item.type === 'SchoolAdmin') && item.name === EventName.LOGIN,
  );
  return newDBLogins;
};

const getLegacyStudentLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate') && item.type === 'Student')
    .filter(
      (item) =>
        item.name === 'user login' ||
        item.name === 'User Login' ||
        item.name === 'interaction end' ||
        item.name === 'User Reactivated',
    );
  return oldDBLogins;
};

const getNewStudentLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter((item) => item.type === 'Student' && item.name === EventName.LOGIN);
  const newPeekaville = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.source === 'mypeekaville',
  );
  const newPlatform = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('peekapakLogin') && item.source === 'peekapak',
  );
  const combinedLogins = [...newPeekaville, ...newPlatform];
  return combinedLogins;
};

export const getLegacyStoryOpenEvents = (events: ReportingMixpanelEvent[]) => {
  const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
  const oldDBStoryOpens = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        item.name.includes('featuredstory') ||
        item.name === 'story-open' ||
        item.name.search(elementaryBookOpenRegexp) >= 0,
    );
  return oldDBStoryOpens;
};

export const getNewStoryOpenEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBStoryOpens = events.filter((item) =>
    [
      EventName.JOURNAL_BOOK_OPEN,
      EventName.JOURNAL_ASSIGNMENT_STORY_OPEN,
      EventName.LESSONS_STORY_OPEN,
      EventName.LESSONS_BOOK_OPEN,
    ].includes(item.name),
  );
  return newDBStoryOpens;
};

const getLegacyLessonsAccessedEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLessons = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        (item.name.includes('lessonPlan') && item.name.includes('readAloud')) ||
        (item.name.includes('lessonPlan') && secondaryUnits.some((v) => item.name.includes(v))),
    );
  return oldDBLessons;
};

const getNewLessonsAccessedEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLessons = events.filter((item) => item.name === EventName.LESSONS_OPEN);
  return newDBLessons;
};

export const getLegacyTimeOnPlatformEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBSessionEnds = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction end' || item.name === 'Exit Website');
  // const totalSessionDuration = Math.floor(
  //   oldDBSessionEnds.reduce(
  //     (acc, curr) =>
  //       (acc = curr?.duration
  //         ? acc +
  //           (curr.duration > 21600
  //             ? parseInt(curr.duration) / 1000
  //             : parseInt(curr.duration))
  //         : acc),
  //     0
  //   ) / 3600
  // );
  return oldDBSessionEnds;
};

export const getNewTimeOnPlatformEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBSessionEnds = events.filter((item) => item.name === EventName.INTERACTION_END);
  return newDBSessionEnds;
};

export const getNumberOfEducators = (events: ReportingMixpanelEvent[]) => {
  const teacherIds = events
    .filter((item) => item.type === 'Teacher' || item.type === 'SchoolAdmin')
    .map((item) => item.userId);
  const uniqueTeachers = new Set(Object.values(teacherIds));
  return uniqueTeachers.size;
};

export const getNumberOfStudents = (events: ReportingMixpanelEvent[]) => {
  const studentIds = events.filter((item) => item.type === 'Student').map((item) => item.userId);
  const uniqueStudents = new Set(Object.values(studentIds));
  return uniqueStudents.size;
};

export const getLegacyEducatorLogins = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('peekapakLogin'))
    .filter((item) => item.name === 'User Login' || item.name === 'login' || item.name === 'User Reactivated');
  return oldDBLogins.length;
};

export const getNewEducatorLogins = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter(
    (item) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(item.type) && item.name === EventName.LOGIN,
  );
  return newDBLogins.length;
};

export const getLegacyStudentLogins = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate') && item.type === 'Student')
    .filter((item) => ['user login', 'User Login', 'interaction end', 'User Reactivated'].includes(item.name));
  return oldDBLogins.length;
};

export const getNewStudentLogins = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter((item) => item.type === 'Student' && item.name === EventName.LOGIN);
  const newPeekaville = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.source === 'mypeekaville',
  );
  const newPlatform = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('peekapakLogin') && item.source === 'peekapak',
  );
  const combinedLogins = [...newPeekaville, ...newPlatform];
  return combinedLogins.length;
};

export const getLegacyStoryOpens = (events: ReportingMixpanelEvent[]) => {
  const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
  const oldDBStoryOpens = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        item.name.includes('featuredstory') ||
        item.name === 'story-open' ||
        item.name.search(elementaryBookOpenRegexp) >= 0,
    );
  return oldDBStoryOpens.length;
};

export const getNewStoryOpens = (events: ReportingMixpanelEvent[]) => {
  const newDBStoryOpens = events.filter((item) =>
    [
      EventName.JOURNAL_BOOK_OPEN,
      EventName.JOURNAL_ASSIGNMENT_STORY_OPEN,
      EventName.LESSONS_STORY_OPEN,
      EventName.LESSONS_BOOK_OPEN,
    ].includes(item.name),
  );
  return newDBStoryOpens.length;
};

export const getLegacyLessonsAccessed = (events: ReportingMixpanelEvent[]) => {
  const oldDBLessons = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        (item.name.includes('lessonPlan') && item.name.includes('readAloud')) ||
        (item.name.includes('lessonPlan') && secondaryUnits.some((v) => item.name.includes(v))),
    );
  return oldDBLessons.length;
};

export const getNewLessonsAccessed = (events: ReportingMixpanelEvent[]) => {
  const newDBLessons = events.filter((item) => item.name === 'lessons-open');
  return newDBLessons.length;
};

export const getLegacyTimeOnPlatform = (events: ReportingMixpanelEvent[]) => {
  const oldDBSessionEnds = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction end' || item.name === 'Exit Website');
  const totalSessionDuration = Math.ceil(
    oldDBSessionEnds.reduce(
      (acc, curr) =>
        (acc = curr?.duration
          ? acc + (curr.duration > 21600 ? parseInt(curr.duration) / 1000 : parseInt(curr.duration))
          : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

export const getNewTimeOnPlatform = (events: ReportingMixpanelEvent[]) => {
  const newDBSessionEnds = events
    .filter((item) => new Date(item.time) >= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction-end');
  const totalSessionDuration = Math.ceil(
    newDBSessionEnds.reduce(
      (acc, curr) =>
        (acc = curr?.duration ? acc + (curr.duration > 21600 ? curr.duration / 1000 : curr.duration) : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

const getTimePeriods = (
  startDate: Date,
  endDate: Date,
  minBarWidth: number,
  gapWidth: number,
  containerWidth: number,
) => {
  interface Span {
    // how many of each span, e.g. 73 days or 11 weeks or 3 months
    count: number;
    // how many days in each count of span, 1 day, 7 days, 30 days
    numberOfDaysInCount: number;
  }

  const daysApart = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

  const spans: [Span, Span, Span, Span, Span] = (() => {
    const days = daysApart;
    const weeks = Math.ceil(days / 7);
    const months = Math.ceil(weeks / 4);
    const quarters = Math.ceil(months / 3);
    const years = Math.ceil(months / 12);

    return [
      { count: days, numberOfDaysInCount: 1 },
      { count: weeks, numberOfDaysInCount: 7 },
      { count: months, numberOfDaysInCount: 30 },
      { count: quarters, numberOfDaysInCount: 90 },
      { count: years, numberOfDaysInCount: 365 },
    ];
  })();

  for (const span of spans) {
    const { count, numberOfDaysInCount } = span;
    const totalWidth = count * minBarWidth + (count - 1) * gapWidth;
    if (totalWidth <= containerWidth) {
      logDebugLevel('getTimePeriods done');
      return getPeriods(startDate, endDate, numberOfDaysInCount);
    }
  }

  throw new Error('Chart width is too small to fit any time periods for data display');

  // TODO: handle timezone offset, since data is recorded in GMT
  function getPeriods(startDate: Date, endDate: Date, numberOfDaysInCount: number) {
    const timePeriods = [];
    let currentDate = endDate;
    let i = 0;
    while (currentDate >= startDate) {
      const from = new Date(currentDate.setHours(0, 0, 0, 0));
      const to = new Date(currentDate.setHours(23, 59, 59, 999));
      from.setDate(from.getDate() - numberOfDaysInCount);
      if (from <= startDate) {
        from.setDate(startDate.getDate());
        from.setMonth(startDate.getMonth());
        from.setFullYear(startDate.getFullYear());
        from.setHours(0, 0, 0, 0);
        timePeriods.push({ from, to });
        break;
      }
      timePeriods.push({ from, to });
      currentDate = from;
      i++;
    }
    return timePeriods;
  }
};

const filterByTime = (data: ReportingMixpanelEvent[], timePeriod: { from: Date; to: Date }) => {
  return data.filter((item) => item.time >= timePeriod.from.getTime() && item.time <= timePeriod.to.getTime());
};

export function getChartData(params: ChartDataCalculationParams) {
  const { data, startDate, endDate, schoolList, minBarWidth, gapWidth, containerWidth } = params;

  const timePeriods = getTimePeriods(startDate, endDate, minBarWidth, gapWidth, containerWidth);

  const dataByTimePeriod = timePeriods.map((timePeriod) => {
    const newDataObject = {
      number_of_educators: filterByTime(data.number_of_educators, timePeriod),
      educator_logins: filterByTime(data.educator_logins, timePeriod),
      student_logins: filterByTime(data.student_logins, timePeriod),
      story_opens: filterByTime(data.story_opens, timePeriod),
      lessons_accessed: filterByTime(data.lessons_accessed, timePeriod),
      time_on_platform: filterByTime(data.time_on_platform, timePeriod),
    };
    return { timePeriod: timePeriod, data: newDataObject };
  });

  const dataByTimeAndSchool = dataByTimePeriod?.map((timeSpan, index) => {
    const dataBySchool = getNewDistrictDataBySchool(timeSpan.data, schoolList);
    return {
      timePeriod: timeSpan.timePeriod,
      data: dataBySchool,
      index: index,
    };
  });
  return dataByTimeAndSchool.reverse();
}

export function parseChartData(
  data: { timePeriod: { from: Date; to: Date }; data: DistrictDataBySchool }[],
  eventToChart: string,
  topFiveSchools: string[],
) {
  const chartData = data.map((timeSpan) => {
    const timePeriod = timeSpan.timePeriod;
    const dataBySchool = timeSpan.data;
    const topFiveSchoolData = topFiveSchools.reduce((acc, curr) => {
      const schoolData = dataBySchool.find((item) => item.school_name === curr);
      return {
        ...acc,
        [curr]: schoolData ? schoolData[eventToChart as string as keyof Omit<DistrictData, 'school_name'>] : 0,
      };
    }, {} as DistrictData);
    const otherData = dataBySchool.filter((item) => !topFiveSchools.includes(item.school_name));
    const otherDataTotal = otherData.reduce(
      (acc, curr) => acc + curr[eventToChart as string as keyof Omit<DistrictData, 'school_name'>],
      0,
    );
    const otherDataFormatted = { 'All Other Schools': otherDataTotal };
    const timePeriodData = {
      name: `${timePeriod.from.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        //year: 'numeric',
      })}`,
      ...topFiveSchoolData,
      ...otherDataFormatted,
    };
    return timePeriodData;
  });

  return chartData;
}

export function getTopFiveSchools(data: DistrictDataBySchool, eventToChart: string) {
  const otherAndEmptyRemoved = data.filter(
    (item) => item.school_name !== 'Other' && item[eventToChart as keyof DistrictData] !== 0,
  );
  const sortedData = otherAndEmptyRemoved.sort(
    (a, b) => Number(b[eventToChart as keyof DistrictData]) - Number(a[eventToChart as keyof DistrictData]),
  );
  const topFive = sortedData.slice(0, 5);
  const topFiveNames = topFive.map((item) => item.school_name);
  return topFiveNames;
}

const getSchoolChartDataByUserType = (data: ReportingMixpanelEvent[], types: string[]) => {
  const dataByType = types.map((type) => {
    const dataByType = data.filter((item) =>
      type === 'Educators' ? item.type === 'Teacher' || item.type === 'SchoolAdmin' : item.type === 'Student',
    );
    return {
      user_type: type,
      active_users: type === 'Educators' ? getNumberOfEducators(dataByType) : getNumberOfStudents(dataByType),
      user_logins:
        type === 'Educators'
          ? getLegacyEducatorLogins(dataByType) + getNewEducatorLogins(dataByType)
          : getLegacyStudentLogins(dataByType) + getNewStudentLogins(dataByType),
      lessons_accessed:
        type === 'Educators' ? getLegacyLessonsAccessed(dataByType) + getNewLessonsAccessed(dataByType) : 0,
      story_opens: getLegacyStoryOpens(dataByType) + getNewStoryOpens(dataByType),
      time_on_platform: getLegacyTimeOnPlatform(dataByType) + getNewTimeOnPlatform(dataByType),
    };
  });
  return dataByType;
};

export function getSchoolChartData(
  legacyPeekavilleData: ReportingMixpanelEvent[],
  legacyPlatformData: ReportingMixpanelEvent[],
  newMixpanelData: ReportingMixpanelEvent[],
  otherParams: SchoolChartDataCalculationParams,
) {
  logDebugLevel('getSchoolChartData start');
  const { startDate, endDate, minBarWidth, gapWidth, containerWidth } = otherParams;
  const timePeriods = getTimePeriods(startDate, endDate, minBarWidth, gapWidth, containerWidth);

  const combinedEvents = [...legacyPeekavilleData, ...legacyPlatformData, ...newMixpanelData];

  const dataByTimePeriod = timePeriods.map((timePeriod) => {
    const newData = filterByTime(combinedEvents, timePeriod);
    return { timePeriod: timePeriod, data: newData };
  });

  const dataByTimeAndType = dataByTimePeriod?.map((timeSpan, index) => {
    const dataByUserType = getSchoolChartDataByUserType(timeSpan.data, ['Educators', 'Students']);

    return {
      timePeriod: timeSpan.timePeriod,
      data: dataByUserType,
      index: index,
    };
  });
  logDebugLevel('getSchoolChartData ', dataByTimeAndType);
  return dataByTimeAndType.reverse();
}

export const getChartFill = (index: number) => {
  const colors = {
    0: '#012A73',
    1: '#00Afd8',
    2: '#69Be28',
    3: '#ffd100',
    4: '#ff6319',
    5: '#7a7a7a',
  };
  return colors[index as keyof typeof colors];
};

export const getSchoolChartFill = (index: number) => {
  const colors = {
    0: '#012A73',
    1: '#00Afd8',
  };
  return colors[index as keyof typeof colors];
};

export function parseSchoolChartData(
  data: { timePeriod: { from: Date; to: Date }; data: DistrictDataBySchool }[],
  eventToChart: string,
) {
  const chartData = data.map((timeSpan) => {
    const timePeriod = timeSpan.timePeriod;
    const dataByType = timeSpan.data;
    const formattedData = ['Educators', 'Students'].reduce((acc, curr) => {
      const typeData = dataByType.find((item) => item.user_type === curr);
      return {
        ...acc,
        [curr]: typeData ? typeData[eventToChart as string as keyof Omit<DistrictData, 'user_type'>] : 0,
      };
    }, {} as DistrictData);
    const timePeriodData = {
      name: `${timePeriod.from.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        // year: 'numeric',
      })}`,
      ...formattedData,
    };
    return timePeriodData;
  });

  return chartData;
}

export const getStartOfSchoolYear = () => {
  const startOfSchoolYear = new Date();
  startOfSchoolYear.setFullYear(startOfSchoolYear.getFullYear() - (startOfSchoolYear.getMonth() < 6 ? 1 : 0));
  startOfSchoolYear.setMonth(6);
  startOfSchoolYear.setDate(1);
  startOfSchoolYear.setHours(0, 0, 0, 0);
  return startOfSchoolYear;
};

export const getMixpanelDatabaseByDate = (startDate: Date, endDate: Date) => {
  if (startDate > lookupNewDBStartDate('completeDate')) return 'newDB';
  if (endDate < lookupNewDBStartDate('startDate')) return 'legacyDB';
  else return 'mixed';
};

export function getUpdatedDistrictUnifiedScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['district'] === '${districtId}'`;

  return noWhiteSpace` 
    function main() {
        return Events({
          from_date: '${startDate}',
          to_date: '${endDate}',
        })
          .filter(function(event) {
            return (
              ${districtFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
      })
      .map(function(item) {
        const { distinct_id, name, properties, time } = item;

        const duration = properties['duration'];

        const toReturn = {
          time,
          duration: duration === undefined ? 0 : duration,
          distinct_id,
          name,
          lesson: properties['lesson'],
          source: properties['source'],
          userId: properties['userId'],
          classroomId: properties['classroomId'],
          schoolId: properties['schoolId'],
          type: properties['userType'],
        };
        return toReturn;
      });
  }
  `;
}

export function getUpdatedSchoolUnifiedScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['schoolId'] === '${schoolIds}'`;
    }

    let acc = `event.properties['schoolId'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['schoolId'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
  function main() {
    return Events({
      from_date: '${startDate}',
      to_date: '${endDate}',
    })
      .filter(function(event) {
        return (
          ${schoolFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
  })
        .map(function(item) {
    const { distinct_id, name, properties, time, source } = item;

    const toReturn = {
      time,
      duration: properties['duration'],
      distinct_id,
      name,
      lesson: properties['lesson'],
      mood: properties['mood'],
      source: properties['source'],
      userId: properties['userId'],
      classroomId: properties['classroomId'],
      schoolId: properties['schoolId'],
      type: properties['userType'],
      grade: properties['grade'],
    };
    return toReturn;
  });
    }
`;
}

export function getLegacyDistrictPlatformScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['District'] === '${districtId}'`;

  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${districtFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const toReturn = {
    duration: properties['Time Spent'],
    schoolId: properties['School ID'],
    time,
    distinct_id,
    name,
    type: properties['Type'],
  };

  return toReturn;
});
    }
`;
}

export function getLegacyDistrictPeekavilleScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['district'] === '${districtId}'`;

  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${districtFilter}
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const {
    classroomId,
    district,
    schoolId,
    type,
    eventName,
    userId,
  } = properties;

  const duration = properties['$duration'];

  const toReturn = {
    time,
    distinct_id,
    name,
    duration: duration === undefined ? 0 : duration,
    schoolId,
    userId,
    type,
  };

  if (eventName) {
    toReturn.eventName = eventName;
  }

  return toReturn;
});
    }
`;
}

export function getLegacySchoolPlatformScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const endDateAsDate = new Date(endDate);
  const queryEndDate = (() => {
    if (endDateAsDate > lookupNewDBStartDate('platformEvents')) {
      return lookupNewDBStartDate('platformEvents').toISOString().split('T')[0];
    }
    return endDate;
  })();

  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['School ID'] === '${schoolIds}'`;
    }

    let acc = `event.properties['School ID'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['School ID'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${queryEndDate}',
  })
    .filter(function(event) {
      return (
        ${schoolFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const toReturn = {
    time,
    duration: properties['Time Spent'],
    distinct_id,
    name,
    userId: properties['User ID'],
    classroomId: properties['Classroom ID'],
    email: properties['Email'],
    type: properties['Type'],
  };

  return toReturn;
});
    }
`;
}

export function getLegacySchoolPeekavilleScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['schoolId'] === '${schoolIds}'`;
    }

    let acc = `event.properties['schoolId'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['schoolId'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${schoolFilter}
              );
})
            .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const {
    classroomId,
    district,
    schoolId,
    type,
    eventName,
    userId,
  } = properties;

  const duration = properties['$duration'];

  const toReturn = {
    time,
    distinct_id,
    name,
    duration: duration === undefined ? 0 : duration,
    schoolId,
    userId,
    type,
  };

  if (eventName) {
    toReturn.eventName = eventName;
  }

  return toReturn;
});
        }
`;
}
